import { alertBar } from "client_legacy/ndaw/alertBar";
import { localize } from "client_legacy/ndaw/culture";
import {
	CalendarDestinationApp,
	ClientlessRdpDestinationWindow,
	DestinationWindow,
	EmailDestinationApp
} from "client/Api/Apps/common";
import { DocumentKind } from "client/Api/Documents/SharePoint/common";
import { never, UnreachableCaseError } from "client_legacy/ndaw/errors";
import { openInline } from "client_legacy/ndaw/fileDownloader";
import { userConfiguration, documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	createUrlRelativeToEnvironment,
	navigateTo,
	navigateToUriInPopOutWindow
} from "client_legacy/ndaw/navigator";
import { showEmailNotConfiguredDialog } from "client/Features/Email/Dialogs/EmailNotConfiguredDialog";
import { IntegrationCredentialsLinkKind } from "client/Api/IntegrationCredentials/integrationCredentials";
import { WorkspaceEmailClient, OwaEmailClient, getEffectiveOwaEmailClient } from "client_legacy/Email/emailClient";
import { ToDoListIdentifierKind } from "client/Api/Workspace/LiveInformation/ToDos/toDoListLiveInformation";
import { navigateToToDoList } from "client/Features/Workspace/Tiles/ToDo/navigateToToDoList";
import { navigateToOwaPeople } from "client/Features/Email/OwaNavigation/owaNavigator";
import { isOpenDocumentDialogSupported, openOpenDocumentDialog } from "client/Features/Documents/Dialogs/OpenDocumentDialog";
import { getAddressBookUrl } from "client/Features/AddressBook/routing";
import { AddressBookContactSource } from "client/Features/AddressBook/Overview/Overview";
import { getHubUrlForPage } from "client/Features/Hub/HubOverviewPage";
import {
	LinkKind,
	openLinkToIntegrationCredentialsDialog
} from "client/Features/Settings/IntegrationCredentials/LinkToIntegrationCredentialsDialog";
import { offerPasswordUpdateWhenPossibleChanged } from "client/Features/Settings/IntegrationCredentials/SyncPassword";
import { openAlertDialog } from "client/Components";
import {
	CalendarClient,
	OwaCalendarClient,
	WorkspaceCalendarClient
} from "client/Features/Calendar/calendarClient";
import { getNedapOnsOrganisationName } from "client/Api/Apps/EditApp/appParameters";
import { hasResource } from "client/Api/Workspace/WindowsVirtualDesktop/windowsVirtualDesktop";
import { openNoDocumentStoresConfiguredDialog } from "client/Features/Documents/NoDocumentStoresConfiguredDialog";
import { RemoteDesktopProtocolClientKind } from "client/Api/Apps/EditApp/saveApp";
import { isNotNil } from "client/Misc/utils";

function navigateToServerRedirectApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	const url: string = createUrlRelativeToEnvironment(`/titlebar/navigation/app/${id}`);
	navigateTo(url, destinationWindow === DestinationWindow.NewWindow);
}

function navigateToOfficeApp(
	documentKind: DocumentKind
) {
	if (isOpenDocumentDialogSupported(documentKind)) {
		openOpenDocumentDialog({ documentKind });
	} else {
		const { isAdmin } = userConfiguration();

		if (isAdmin) {
			openNoDocumentStoresConfiguredDialog(true);
		} else {
			alertBar.showTextAlert(localize("_DocumentsIsNotConfiguredUserBody"), "error");
		}
	}
}

function navigateToAddressBookApp(options: { source?: AddressBookContactSource; } = {}): void {
	const { source } = options;
	const url: string = getAddressBookUrl(
		isNotNil(source)
			? { source }
			: undefined
	);

	navigateTo(url);
}

function navigateToAfasApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToAutotaskApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToAzureApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToCalendarApp(
	{ destinationWindow, destinationApp }: { destinationWindow: DestinationWindow; destinationApp: CalendarDestinationApp; },
	onNavigate?: () => void
) {
	const { exchange: { isEnabled, isConfigured } } = userConfiguration();

	if (isEnabled && isConfigured) {
		let calendarClient: CalendarClient;
		switch (destinationApp) {
			case CalendarDestinationApp.Workspace:
				calendarClient = new WorkspaceCalendarClient(destinationWindow === DestinationWindow.NewWindow);
				break;
			case CalendarDestinationApp.OutlookWebApp:
				calendarClient = new OwaCalendarClient(destinationWindow === DestinationWindow.NewWindow);
				break;
			default:
				never(destinationApp);
		}
		if (onNavigate) {
			onNavigate();
		}

		calendarClient.openOverview();
	} else {
		showEmailNotConfiguredDialog(false, !isEnabled);
	}
}

function navigateToCitrixXenApp(
	{ id }: { id: number; }
) {
	navigateToServerRedirectApp({ id, destinationWindow: DestinationWindow.NewWindow });
}

async function navigateToClientlessRdpApp(
	{ id, destinationWindow }: { id: number; destinationWindow: ClientlessRdpDestinationWindow; },
	onNavigate?: () => void
): Promise<void> {
	const callback = () => {
		if (onNavigate) {
			onNavigate();
		}

		const url: string = createUrlRelativeToEnvironment(`/titlebar/navigation/app/${id}`);
		if (destinationWindow === ClientlessRdpDestinationWindow.NewWindow) {
			navigateTo(url, true);
		} else {
			navigateToUriInPopOutWindow(url);
		}
	};

	const { integrationCredentialsConfiguredLinks } = userConfiguration();
	const hasIntegrationCredentialsConfigured = integrationCredentialsConfiguredLinks.environmentApps
		.indexOf(id) !== -1;

	if (!hasIntegrationCredentialsConfigured) {
		const result: TryResult<void> = await openLinkToIntegrationCredentialsDialog({
			thingToLink: {
				kind: LinkKind.EnvironmentApp,
				id: id
			}
		});

		if (!result.wasSuccessful) {
			return;
		}

		callback();
	} else {
		await offerPasswordUpdateWhenPossibleChanged({
			kind: IntegrationCredentialsLinkKind.EnvironmentApp,
			appId: id
		});

		callback();
	}
}

function navigateToConnectWiseApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToDocumentsApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToEmailApp(
	{ destinationWindow, destinationApp }: { destinationWindow: DestinationWindow; destinationApp: EmailDestinationApp; },
	options: { folderId?: string; mailbox?: string | null; } = {},
	onNavigate?: () => void
) {
	let isCompleted = true;
	const { exchange: { isEnabled, isConfigured } } = userConfiguration();
	if (isEnabled && isConfigured) {

		let emailClient: WorkspaceEmailClient | OwaEmailClient;
		switch (destinationApp) {
			case EmailDestinationApp.Email:
				emailClient = new WorkspaceEmailClient(destinationWindow === DestinationWindow.NewWindow);
				break;
			case EmailDestinationApp.OutlookWebApp:
				emailClient = getEffectiveOwaEmailClient(destinationWindow === DestinationWindow.NewWindow);
				break;
			default:
				throw new UnreachableCaseError(destinationApp);
		}

		if (onNavigate) {
			onNavigate();
		}

		emailClient.openOverview({
			...options
		});

		return isCompleted;
	} else {
		isCompleted = false;

		showEmailNotConfiguredDialog(false, !isEnabled);

		return isCompleted;
	}
}

function navigateToEmbedApp(
	{ id }: { id: number; }
) {
	navigateToServerRedirectApp({ id, destinationWindow: DestinationWindow.CurrentWindow });
}

function navigateToExcelApp() {
	navigateToOfficeApp(DocumentKind.Excel);
}

function navigateToGoogleApp({ destinationWindow }: { destinationWindow: DestinationWindow; }) {
	const GOOGLE_URL = "https://www.google.com/";
	navigateTo(GOOGLE_URL, destinationWindow === DestinationWindow.NewWindow);
}

function navigateToHubApp(): void {
	const url: string = getHubUrlForPage();
	navigateTo(url);
}

function navigateToLiquitApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToLocalApp(
	{ id }: { id: number; }
) {
	navigateToServerRedirectApp({ id, destinationWindow: DestinationWindow.CurrentWindow });
}

function navigateToLyncApp() {
	navigateTo("https://sched.lync.com/", true);
}

export function navigateToMagisterApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

async function navigateToNedapOnsApp(
	{
		id,
		destinationWindow,
		view = "portal"
	}: {
		id: number;
		destinationWindow: DestinationWindow;
		view?: "portal" | "clients" | "planning" | "roster";
	},
	onNavigate?: () => void
): Promise<void> {
	let organisationName: string;
	try {
		organisationName = await getNedapOnsOrganisationName(id);
	} catch (e) {
		console.error(e);
		alertBar.showTextAlert(localize("_GeneralErrorMessage"), "error");
		return;
	}

	let url: string;
	switch (view) {
		case "portal":
			url = `https://${encodeURIComponent(organisationName)}.mijnio.nl`;
			break;
		case "clients":
			url = `https://${encodeURIComponent(organisationName)}.ons-dossier.nl/client`;
			break;
		case "planning":
			url = `https://${encodeURIComponent(organisationName)}.hasmoves.com/onsdraaiboek/planning`;
			break;
		case "roster":
			url = `https://${encodeURIComponent(organisationName)}.hasmoves.com/onsdraaiboek/roster`;
			break;
		default:
			throw new UnreachableCaseError(view);
	}

	if (onNavigate) {
		onNavigate();
	}

	navigateTo(url, destinationWindow === DestinationWindow.NewWindow);
}

function navigateToOneNoteApp() {
	navigateToOfficeApp(DocumentKind.OneNote);
}

function navigateToPeopleApp(onNavigate?: ()=> void) {
	const { exchange: { isEnabled, isConfigured } } = userConfiguration();
	if (isEnabled && isConfigured) {
		if (onNavigate) {
			onNavigate();
		}

		navigateToOwaPeople();
	} else {
		showEmailNotConfiguredDialog(false, !isEnabled);
	}
}

function navigateToPowerPointApp() {
	navigateToOfficeApp(DocumentKind.PowerPoint);
}

function navigateToSharePointApp(
	{ destinationWindow }: { destinationWindow: DestinationWindow; },
	onNavigate?: () => void
) {
	const { sharePoint: { isConfigured, rootUrl } } = documentsConfiguration();
	if (!isConfigured) {
		openAlertDialog({
			title: localize("_YouDontHaveSharePointConfiguredYet"),
			confirmText: localize("_Ok"),
			trackingId: "no-sp-configured-dialog"
		});
		return;
	}

	if (onNavigate) {
		onNavigate();
	}

	navigateTo(rootUrl, destinationWindow === DestinationWindow.NewWindow);
}

function navigateToShortcutApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToTeamsApp({ destinationWindow }: { destinationWindow: DestinationWindow; }) {
	const TEAMS_URL = "https://teams.microsoft.com/";
	navigateTo(TEAMS_URL, destinationWindow === DestinationWindow.NewWindow);
}

function navigateToToDoApp(
	{ destinationWindow }: { destinationWindow: DestinationWindow; },
	onNavigate?: () => void
) {
	navigateToToDoList(
		{ kind: ToDoListIdentifierKind.Default },
		{ openInNewWindow: destinationWindow === DestinationWindow.NewWindow },
		onNavigate
	);
}

function navigateToTopDeskApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToVilansKickApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

export type WindowsVirtualDesktopAppNavigationParameters = {
	id: number;
	client: RemoteDesktopProtocolClientKind;
};

async function navigateToWindowsVirtualDesktopApp(
	parameters: WindowsVirtualDesktopAppNavigationParameters
): Promise<void> {
	const { id, client } = parameters;

	try {
		const hasAccess: boolean = await hasResource(id);

		if (!hasAccess) {
			alertBar.showTextAlert(localize("_ThisApplicationIsNotAvailableForYouPleaseContactYourAdminToGetAccessToThisApplication"), "error");
			return;
		}

		switch (client) {
			case RemoteDesktopProtocolClientKind.MicrosoftRemoteDesktopClient:
			case RemoteDesktopProtocolClientKind.MicrosoftTerminalServicesClient:
				openInline(createUrlRelativeToEnvironment(`Workspace/WindowsVirtualDesktop/RdpFile/${id}`));
				break;
			case RemoteDesktopProtocolClientKind.Web:
				navigateTo(
					createUrlRelativeToEnvironment(`Workspace/WindowsVirtualDesktop/VirtualDesktop/${id}`),
					true
				);
				break;
			case RemoteDesktopProtocolClientKind.WindowsApp:
				navigateTo(
					createUrlRelativeToEnvironment(`Workspace/WindowsVirtualDesktop/VirtualDesktop/${id}`),
					false
				);
				break;
			default:
				never(client);
		}
	} catch (e) {
		console.error(e);
		alertBar.showTextAlert(localize("_GeneralErrorMessage"), "error");
	}
}

function navigateToWordApp() {
	navigateToOfficeApp(DocumentKind.Word);
}

function navigateToVmwareHorizonApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

function navigateToYammerApp() {
	const YAMMER_URL = "https://www.yammer.com";
	navigateTo(YAMMER_URL, true);
}

function navigateToZaaksysteemApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

export function navigateToZermeloApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

export function navigateToZenyaDocApp(
	{ id, destinationWindow }: { id: number; destinationWindow: DestinationWindow; }
) {
	navigateToServerRedirectApp({ id, destinationWindow });
}

export function navigateToIntegrationApp(
	url: string,
	destinationWindow: DestinationWindow
): void {
	navigateTo(url, destinationWindow === DestinationWindow.NewWindow);
}

export {
	navigateToAddressBookApp,
	navigateToAfasApp,
	navigateToAutotaskApp,
	navigateToAzureApp,
	navigateToCalendarApp,
	navigateToCitrixXenApp,
	navigateToClientlessRdpApp,
	navigateToConnectWiseApp,
	navigateToDocumentsApp,
	navigateToEmailApp,
	navigateToEmbedApp,
	navigateToExcelApp,
	navigateToGoogleApp,
	navigateToHubApp,
	navigateToLiquitApp,
	navigateToLocalApp,
	navigateToLyncApp,
	navigateToNedapOnsApp,
	navigateToOneNoteApp,
	navigateToPeopleApp,
	navigateToPowerPointApp,
	navigateToSharePointApp,
	navigateToShortcutApp,
	navigateToTeamsApp,
	navigateToToDoApp,
	navigateToTopDeskApp,
	navigateToVilansKickApp,
	navigateToVmwareHorizonApp,
	navigateToWindowsVirtualDesktopApp,
	navigateToWordApp,
	navigateToYammerApp,
	navigateToZaaksysteemApp
};
